/**
 * Strips identifiers and credentials out of a URL before it is sent to
 * Google Analytics as `page_path`.
 *
 * This is not a nicety. `AppContainer` mounts `PageViewTracker` as the layout
 * for `/`, and the auth routes are its children, so every password reset and
 * email verification published its one-time token to Analytics. The weather
 * map writes `longitude`/`latitude`/`zoom` into the query string on every
 * camera move, and the station overlay writes `station=<id>`, so a user's
 * precise location went the same way.
 *
 * It also makes the reports usable: `/app/stations/48215` is one row per
 * station, which is thousands of rows saying nothing. `/app/stations/:id` is
 * one row that answers "how often is a station opened".
 */

/**
 * Applied in order, each against the start of the path. Suffixes are
 * preserved, so `/app/stations/48215/sensors/T` keeps its tail.
 *
 * Case-insensitive because React Router's matching is: `compilePath` defaults
 * `caseSensitive` to false and no route here overrides it, so
 * `/auth/Reset-Password/...` renders the reset screen exactly like the
 * lowercase URL — and a case-sensitive rule would publish its token.
 *
 * Numeric-only for the station id on purpose: `[^/]+` would also swallow the
 * sibling routes `/app/stations/search` and `/app/stations/manage`, turning
 * two meaningful rows into noise. Station ids are database keys.
 */
const RULES: ReadonlyArray<readonly [RegExp, string]> = [
  // The second segment is optional: a mail client that wraps the URL can
  // deliver `/auth/reset-password/<uidb36>` alone, and `uidb36` is a stable
  // per-user identifier even without the token beside it.
  [
    /^\/auth\/reset-password\/[^/]+(\/[^/]+)?/i,
    '/auth/reset-password/:uidb36/:key',
  ],
  [/^\/auth\/verify-email-change\/[^/]+/i, '/auth/verify-email-change/:key'],
  [/^\/auth\/verify-email\/[^/]+/i, '/auth/verify-email/:key'],
  // `(?=\/|$)` so `/app/stations/48215/` does not become `/app/stations/:id/`
  // and open a second row for the same page.
  [/^\/app\/stations\/\d+(?=\/|$)/i, '/app/stations/:id'],
  // A warning id is a database key too: one row per warnings page, not one
  // per warning ever issued. Runs after the station rule, on its output.
  [
    /^\/app\/stations\/:id\/warnings\/\d+(?=\/|$)/i,
    '/app/stations/:id/warnings/:warningId',
  ],
];

/**
 * The query string is dropped wholesale rather than filtered.
 *
 * Nothing in it is currently worth measuring — it carries map coordinates
 * (`longitude`/`latitude`/`zoom`), a station id, a forecast run timestamp and
 * the help-centre `platform` flag. An allowlist would be the right shape the
 * day a query parameter is worth keeping; until then, dropping is the only
 * option that cannot be wrong about a parameter nobody remembered.
 */
export const sanitizePagePath = (path: string): string => {
  const [pathname] = path.split('?');

  return RULES.reduce(
    (current, [pattern, replacement]) => current.replace(pattern, replacement),
    pathname,
  );
};
